import { useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { mdiBellOutline } from '@mdi/js'
import { useBreadcrumbs } from '../../../../hooks/useBreadcrumbs'
import { PageHeader } from '../../../../components/shared/PageHeader'
import { Card, CardContent } from '../../../../components/ui/card'
import { Checkbox } from '../../../../components/ui/checkbox'
import { Button } from '../../../../components/ui/button'
import { Label } from '../../../../components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../../../components/ui/dialog'
import { BrowserNotificationsToggle } from '../../../../components/conversations/BrowserNotificationsToggle'
import { useOwnSetting } from '../../../../hooks/useOwnSetting'
import { $api } from '../../../../api/queryClient'
import {
    EMAIL_DIGEST_SETTING,
    TIME_ZONE_SETTING,
    browserTimeZone,
    readDigest,
    toggleDay,
    weekdayNames,
    type DigestPreference,
} from '../../../../lib/digest'

export const Route = createFileRoute('/$org/user/me/notifications')({
    component: UserNotificationsPage,
})

/**
 * The caller's notifications (spec 2026-10-27-23): the daily e-mail digest
 * (on/off, hour, days, a preview), the time zone it is read in, and this
 * browser's conversation notifications. Every change saves at once through
 * `PUT /users/me/settings/{code}`, with its saving and error states.
 */
function UserNotificationsPage() {
    const { org } = Route.useParams()
    const { t, i18n } = useTranslation('users')

    const breadcrumbs = useMemo(() => [
        { title: t('breadcrumb.home'), path: `/${org}` },
        { title: t('breadcrumb.user'), path: `/${org}/user/me/profile` },
        { title: t('notifications.title') },
    ], [org, t])
    useBreadcrumbs(breadcrumbs)

    const digestSetting = useOwnSetting<Partial<DigestPreference>>(org, EMAIL_DIGEST_SETTING)
    const zoneSetting = useOwnSetting<string>(org, TIME_ZONE_SETTING)
    const digest = readDigest(digestSetting.value)
    const days = useMemo(() => weekdayNames(i18n.language || 'fr'), [i18n.language])
    const browserZone = browserTimeZone()
    const zone = zoneSetting.value || 'UTC'
    const [previewOpen, setPreviewOpen] = useState(false)

    const saveDigest = (next: Partial<DigestPreference>) => void digestSetting.save({ ...digest, ...next }).catch(() => undefined)

    const preview = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/users/me/email-digest/preview',
        { params: { path: { orgCode: org } } },
        { enabled: previewOpen, staleTime: 0 },
    )

    return (
        <div className="space-y-6" data-testid="user-notifications">
            <PageHeader icon={mdiBellOutline} title={t('notifications.title')} description={t('notifications.subtitle')} />

            <Card>
                <CardContent className="space-y-4 pt-6">
                    <div className="flex items-start gap-3">
                        <Checkbox
                            id="notifications-digest-toggle"
                            checked={digest.enabled}
                            disabled={digestSetting.isLoading || digestSetting.isSaving}
                            onCheckedChange={(on) => saveDigest({ enabled: on })}
                            data-testid="notifications-digest-toggle"
                        />
                        <div>
                            <Label htmlFor="notifications-digest-toggle" className="font-medium">
                                {t('notifications.digest_label')}
                            </Label>
                            <p className="text-sm text-muted-foreground">{t('notifications.digest_help')}</p>
                        </div>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-[auto_1fr] sm:items-center">
                        <Label>{t('notifications.hour')}</Label>
                        <Select
                            value={String(digest.hour)}
                            onValueChange={(v) => saveDigest({ hour: Number(v) })}
                            disabled={digestSetting.isSaving}
                        >
                            <SelectTrigger className="w-32" data-testid="notifications-digest-hour">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {Array.from({ length: 24 }, (_, h) => (
                                    <SelectItem key={h} value={String(h)} data-testid={`notifications-digest-hour-${h}`}>
                                        {`${String(h).padStart(2, '0')}:00`}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        <Label>{t('notifications.days')}</Label>
                        <div className="flex flex-wrap gap-3">
                            {days.map(({ day, label }) => (
                                <label key={day} className="flex items-center gap-1.5 text-sm capitalize">
                                    <Checkbox
                                        checked={digest.days.includes(day)}
                                        disabled={digestSetting.isSaving}
                                        onCheckedChange={() => saveDigest({ days: toggleDay(digest.days, day) })}
                                        data-testid={`notifications-digest-day-${day}`}
                                    />
                                    {label}
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        <Button variant="outline" onClick={() => setPreviewOpen(true)} data-testid="notifications-digest-preview">
                            {t('notifications.preview')}
                        </Button>
                        {digestSetting.isSaving && (
                            <span className="text-sm text-muted-foreground" data-testid="notifications-digest-saving">
                                {t('notifications.saving')}
                            </span>
                        )}
                        {digestSetting.saveError && (
                            <span className="text-sm text-destructive" data-testid="notifications-digest-error">
                                {t('notifications.save_error')}
                            </span>
                        )}
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardContent className="space-y-2 pt-6">
                    <p className="font-medium">{t('notifications.time_zone')}</p>
                    <p className="text-sm text-muted-foreground" data-testid="notifications-time-zone">
                        {t('notifications.time_zone_in_use', { zone })}
                    </p>
                    {browserZone && browserZone !== zone && (
                        <Button
                            variant="outline"
                            onClick={() => void zoneSetting.save(browserZone).catch(() => undefined)}
                            disabled={zoneSetting.isSaving}
                            data-testid="notifications-time-zone-use-browser"
                        >
                            {t('notifications.use_browser_zone', { zone: browserZone })}
                        </Button>
                    )}
                    {zoneSetting.isSaving && (
                        <span className="text-sm text-muted-foreground" data-testid="notifications-time-zone-saving">
                            {t('notifications.saving')}
                        </span>
                    )}
                    {zoneSetting.saveError && (
                        <span className="text-sm text-destructive" data-testid="notifications-time-zone-error">
                            {t('notifications.save_error')}
                        </span>
                    )}
                </CardContent>
            </Card>

            <Card>
                <CardContent className="space-y-2 pt-6">
                    <p className="font-medium">{t('notifications.browser')}</p>
                    <p className="text-sm text-muted-foreground">{t('notifications.browser_help')}</p>
                    <BrowserNotificationsToggle />
                </CardContent>
            </Card>

            <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
                <DialogContent className="max-w-3xl" data-testid="notifications-digest-preview-dialog">
                    <DialogHeader>
                        <DialogTitle>{preview.data?.subject ?? t('notifications.preview')}</DialogTitle>
                        <DialogDescription>{t('notifications.preview_help')}</DialogDescription>
                    </DialogHeader>
                    {preview.isLoading && <p className="text-sm text-muted-foreground">{t('notifications.loading')}</p>}
                    {preview.isError && <p className="text-sm text-destructive">{t('notifications.preview_error')}</p>}
                    {preview.data && (
                        <iframe
                            title={t('notifications.preview')}
                            sandbox=""
                            srcDoc={preview.data.html}
                            className="h-[60vh] w-full rounded border bg-white"
                            data-testid="notifications-digest-preview-frame"
                        />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    )
}
